import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Filter } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface NAICSSelectorProps {
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
  maxSelections?: number;
}

interface NAICSCode {
  code: string;
  title: string;
  description: string;
  sector: string;
}

export function NAICSSelector({ selectedCodes, onChange, maxSelections = 30 }: NAICSSelectorProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState<NAICSCode[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load available sectors
  useEffect(() => {
    async function loadSectors() {
      try {
        const { data, error } = await supabase
          .from('naics_codes')
          .select('sector')
          .order('sector');

        if (error) throw error;

        // Get unique sectors and sort alphabetically
        const uniqueSectors = Array.from(new Set(data?.map(item => item.sector))).sort();
        setSectors(uniqueSectors);
      } catch (error) {
        console.error('Error loading sectors:', error);
        toast.error('Failed to load sectors');
      }
    }

    loadSectors();
  }, []);

  useEffect(() => {
    let mounted = true;
    
    async function fetchCodes() {
      try {
        setLoading(true);
        let query = supabase
          .from('naics_codes')
          .select('*')
          .order('code');

        // Apply sector filter if selected
        if (selectedSector) {
          query = query.eq('sector', selectedSector);
        }

        // Apply search filter
        if (debouncedSearch) {
          query = query.or(`code.ilike.%${debouncedSearch}%,title.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%`);
        }

        const { data, error } = await query;
        
        if (error) throw error;
        if (mounted) {
          setCodes(data || []);
        }
      } catch (error) {
        console.error('Error fetching NAICS codes:', error);
        toast.error('Failed to load NAICS codes');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchCodes();

    return () => {
      mounted = false;
    };
  }, [debouncedSearch, selectedSector]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (code: string) => {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter(c => c !== code));
    } else if (selectedCodes.length < maxSelections) {
      onChange([...selectedCodes, code]);
    } else {
      toast.error(`Maximum of ${maxSelections} NAICS codes allowed`);
    }
  };

  return (
    <div className="space-y-4 relative" ref={containerRef}>
      <div>
        <h3 className="text-lg font-medium text-bw-ink">NAICS Codes</h3>
        <p className="mt-1 text-sm text-bw-muted">
          Select up to {maxSelections} NAICS codes that best represent your business capabilities.
          Filter by sector to find relevant codes more easily.
        </p>
      </div>

      {/* Selected Codes */}
      {selectedCodes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCodes.map(code => {
            const naicsCode = codes.find(n => n.code === code);
            return (
              <div
                key={code}
                className="flex items-center bg-bw-primary-tint text-bw-primary px-3 py-1.5 rounded-full text-sm group relative"
              >
                <span className="font-medium">{code}</span>
                <span className="mx-1">-</span>
                <span className="truncate max-w-[200px]">{naicsCode?.title || 'Loading...'}</span>
                <button
                  onClick={() => handleSelect(code)}
                  className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <span className="sr-only">Remove</span>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {/* Tooltip */}
                <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 max-w-xs">
                    <p className="font-medium">{naicsCode?.title}</p>
                    <p className="mt-1">{naicsCode?.description}</p>
                  </div>
                  <div className="absolute left-1/2 -top-1 -translate-x-1/2">
                    <div className="border-4 border-transparent border-b-gray-900"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Sector Filter */}
        <div className="relative">
          <label className="block text-sm font-medium text-bw-ink mb-1">
            Filter by Sector
          </label>
          <div className="relative">
            <select
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-bw-border focus:outline-none focus:ring-bw-primary focus:border-blue-500 rounded-md"
            >
              <option value="">All Sectors</option>
              {sectors.map((sector) => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <Filter className="h-4 w-4 text-bw-muted" />
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative">
          <label className="block text-sm font-medium text-bw-ink mb-1">
            Search NAICS Codes
          </label>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder="Search by code or description..."
              className="block w-full pl-10 pr-4 py-2 border border-bw-border rounded-md focus:outline-none focus:ring-2 focus:ring-bw-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
          </div>
        </div>
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <div className="relative" ref={dropdownRef}>
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 w-full mt-1 bg-bw-surface rounded-lg shadow-xl border border-bw-border max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-bw-muted">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-servet-blue mx-auto mb-2"></div>
                Loading NAICS codes...
              </div>
            ) : codes.length === 0 ? (
              <div className="p-4 text-sm text-bw-muted text-center">
                No NAICS codes found matching your criteria
              </div>
            ) : (
              <div className="py-2">
                {codes.map(code => {
                  const isSelected = selectedCodes.includes(code.code);
                  const isDisabled = selectedCodes.length >= maxSelections && !isSelected;

                  return (
                    <button
                      key={code.code}
                      onClick={() => !isDisabled && handleSelect(code.code)}
                      className={`w-full text-left px-4 py-3 hover:bg-bw-surface-subtle focus:outline-none focus:bg-bw-surface-subtle relative ${
                        isSelected ? 'bg-bw-primary-tint' : ''
                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <span className="font-medium text-bw-ink">{code.code}</span>
                            <span className="mx-2 text-bw-muted">|</span>
                            <span className="text-bw-ink">{code.title}</span>
                          </div>
                          <p className="mt-1 text-sm text-bw-muted line-clamp-2">
                            {code.description}
                          </p>
                          <p className="mt-1 text-xs text-bw-primary">
                            Sector: {code.sector}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {}}
                          className="h-4 w-4 text-bw-primary border-bw-border rounded focus:ring-bw-primary"
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}